import { DashboardDeviceLabel } from 'App/dashboards/_components/DashboardDeviceLabel'
import {
  DashboardGroupedTable,
  type TGroupedTableColumn,
} from 'App/dashboards/_components/DashboardGroupedTable'
import { useFlowMeterRate, type TFlowMeterRow } from 'App/dashboards/_utils/useFlowMeterRate'
import { translate } from 'i18n/i18n'
import type moment from 'moment-timezone'
import { useEffect, useMemo } from 'react'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { unitConverter } from 'utils/unitConverter/unitConverter'

// Empty string for the CSV value (CSV blank = missing); the table renders a dash instead.
const formatFlow = (gallons: number | null): string =>
  gallons === null ? '' : unitConverter.flowMeter(gallons).valueWithSuffix()

const formatDuration = (ms: number | null): string =>
  ms === null ? '' : minutesToHoursAndMinutes(ms / 60000)

const formatFlowRate = (gpm: number | null): string =>
  gpm === null ? '' : unitConverter.flowRate(gpm).valueWithSuffix()

export const FlowMeterRate = ({
  propertyIds,
  dateFrom,
  dateTo,
  refreshToken,
  onLoaded,
}: {
  propertyIds: number[]
  dateFrom: moment.Moment
  dateTo: moment.Moment
  refreshToken?: number
  // Reports the widget's last successful fetch time up to the shell's "Last Updated".
  onLoaded?: (date: Date) => void
}) => {
  const { sections, loading, error, lastFetchedAt } = useFlowMeterRate({
    propertyIds,
    dateFrom,
    dateTo,
    refreshToken,
  })

  useEffect(() => {
    if (lastFetchedAt) onLoaded?.(lastFetchedAt)
  }, [lastFetchedAt, onLoaded])

  // Flow-meter access is per-property — tag each property's section so it shows a "no access" vs
  // "no data" message accordingly. `permissions` is in the deps so this recomputes once they load.
  const permissions = userDetailsStore.useSelector((s) => s.permissions)

  const sectionsWithAccess = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        hasAccess: checkAuthorization({
          permission: 'VIEW_IRRIGATION_FLOW_METER_DETAILS',
          entity: section.propertyId,
        }),
      })),
    [sections, permissions],
  )

  const columns = useMemo<TGroupedTableColumn<TFlowMeterRow>[]>(
    () => [
      {
        key: 'device',
        header: translate.phrases.banyanApp('Device'),
        value: (row) => row.deviceName,
        render: (row) => (
          <DashboardDeviceLabel name={row.deviceName} externalVendorNames={row.externalVendorNames} />
        ),
      },
      {
        key: 'cumulativeFlow',
        header: translate.phrases.banyanApp('Cum. Flow'),
        value: (row) => formatFlow(row.cumulativeGallons),
        render: (row) => formatFlow(row.cumulativeGallons) || '-',
      },
      {
        key: 'duration',
        header: translate.phrases.banyanApp('On Duration'),
        value: (row) => formatDuration(row.onDurationMs),
        render: (row) => formatDuration(row.onDurationMs) || '-',
      },
      {
        key: 'averageFlowRate',
        header: translate.phrases.banyanApp('Avg Flow Rate'),
        value: (row) => formatFlowRate(row.averageFlowRateGpm),
        render: (row) => formatFlowRate(row.averageFlowRateGpm) || '-',
      },
    ],
    [],
  )

  // Always render the card; each property's section shows data, a "no access" message (per-property
  // flow-meter permission), or "no data" — matching the Hub pattern (e.g. Historical Trap Counts).
  return (
    <DashboardGroupedTable
      title={translate.phrases.banyanApp('Flow Meter Rate')}
      sections={sectionsWithAccess}
      columns={columns}
      loading={loading}
      error={error}
      downloadFileName={translate.phrases.banyanApp('Flow Meter Rate')}
    />
  )
}
